import moment from "moment";
import {
  FisikReasesmenKunci,
  ObatAktifItem,
  PerkembanganCpptItem,
  TtvReasesmenKunci
} from "@/types/asesmen_awal_ranap_medis";

export const SOURCES_REASESMEN = [
  "klinis_terkini",
  "asesmen_ranap_medis_sebelumnya",
  "cppt_dpjp",
  "obat_aktif"
];

export const LABEL_FISIK: Record<FisikReasesmenKunci, string> = {
  kepala: "Kepala",
  leher: "Leher",
  dada: "Dada / Thorax",
  jantung: "Jantung",
  paru: "Paru",
  perut: "Perut",
  ekstremitasAtas: "Ekstremitas Atas",
  ekstremitasBawah: "Ekstremitas Bawah",
  statusPubertas: "Status Pubertas",
  genital: "Genital",
  tubuhBelakang: "Tubuh Belakang"
};

export const JALUR_TTV: Record<string, TtvReasesmenKunci> = {
  "tanda_vital.sistole": "sistole",
  "tanda_vital.diastole": "diastole",
  "tanda_vital.frekuensi_nadi": "nadi",
  "tanda_vital.frekuensi_nafas": "nafas",
  "tanda_vital.suhu_badan": "suhu",
  "tanda_vital.saturasi_oksigen": "saturasiO2",
  "tanda_vital.keadaan_umum": "keadaanUmum",
  "antropometri.berat_badan": "bb",
  "antropometri.tinggi_badan": "tb"
};

const ALIAS_FISIK: Array<[RegExp, FisikReasesmenKunci]> = [
  [/^kepala$/, "kepala"],
  [/^leher$/, "leher"],
  [/^(dada|thorax|thoraks|toraks|dada \/ thorax)$/, "dada"],
  [/^(jantung|cor)$/, "jantung"],
  [/^(paru|paru-paru|pulmo)$/, "paru"],
  [/^(perut|abdomen)$/, "perut"],
  [/^ekstremitas atas$/, "ekstremitasAtas"],
  [/^ekstremitas bawah$/, "ekstremitasBawah"],
  [/^status pubertas$/, "statusPubertas"],
  [/^(genital|genitalia)$/, "genital"],
  [/^tubuh belakang$/, "tubuhBelakang"]
];

export function parseObjektifFisik(
  teks: string | null | undefined
): { fisik: Partial<Record<FisikReasesmenKunci, string>>; sisa: string } {
  const fisik: Partial<Record<FisikReasesmenKunci, string>> = {};
  const sisa: string[] = [];

  String(teks || "")
    .split(/\r?\n/)
    .forEach(barisMentah => {
      const baris = barisMentah.trim();
      if (!baris) return;

      const cocok = baris.match(/^([^:]{2,40}):\s*(.*)$/);
      if (cocok) {
        const label = cocok[1].trim().toLowerCase().replace(/\s+/g, " ");
        const isi = cocok[2].trim();
        const alias = ALIAS_FISIK.find(([pola]) => pola.test(label));

        if (alias && isi) {
          const kunci = alias[1];
          fisik[kunci] = fisik[kunci] ? `${fisik[kunci]}; ${isi}` : isi;
          return;
        }
      }

      sisa.push(baris);
    });

  return { fisik, sisa: sisa.join("\n") };
}

function rata(teks: string | null | undefined): string {
  return String(teks || "")
    .split(/\r?\n/)
    .map(baris => baris.trim())
    .filter(Boolean)
    .join("; ");
}

export function barisPerkembangan(item: PerkembanganCpptItem): string {
  const tanggal = item.tanggal
    ? moment(item.tanggal, "YYYY-MM-DD HH:mm:ss").format("DD-MM-YYYY")
    : "-";
  const isi = [rata(item.s), item.a ? `A: ${rata(item.a)}` : ""]
    .filter(Boolean)
    .join(" — ");

  return `${tanggal}: ${isi}`;
}

export function susunRps(
  rpsAwal: string | null,
  perkembangan: PerkembanganCpptItem[]
): string {
  const bagian: string[] = [];

  if (rpsAwal && rpsAwal.trim()) bagian.push(rpsAwal.trim());
  if (perkembangan.length) {
    bagian.push(
      ["Perkembangan selama dirawat:"]
        .concat(perkembangan.map(barisPerkembangan))
        .join("\n")
    );
  }

  return bagian.join("\n\n");
}

export function susunTerapi(
  terapi: string | null,
  obat: ObatAktifItem[]
): string {
  const bagian: string[] = [];

  if (terapi && terapi.trim()) bagian.push(terapi.trim());
  if (obat.length) {
    bagian.push(
      ["Obat aktif:"].concat(obat.map(item => `- ${item.label}`)).join("\n")
    );
  }

  return bagian.join("\n\n");
}

export function formatWaktuSumber(nilai: string | null | undefined): string {
  if (!nilai) return "";
  const waktu = moment(nilai, moment.ISO_8601);
  return waktu.isValid() ? waktu.format("DD-MM-YYYY HH:mm") : "";
}
